import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { Label } from "./annotate";

/**
 * Anivaze v4 — CIRCUIT actor (Animation-Lite pack; REFERENCE-STUDY §4.1).
 *
 * A real DC circuit: a battery drives current CLOCKWISE around a wire loop
 * through a load (bulb / resistor / motor), with a switch on the return leg.
 * The "one verb" (Rule 2) is CURRENT FLOWING — dots travel the loop as a pure
 * function of `t`. Closing the switch lights the bulb; opening it stops the
 * current and darkens the load. Parts are leader-labeled (Rule 3).
 *
 * Director drives it via items = [component, state], e.g. ["bulb","on"] or
 * ["resistor","off"] — parsed from the beat text by parseCircuitConfig.
 */

const TAU = Math.PI * 2;

export interface CircuitConfig {
  component: "bulb" | "resistor" | "motor";
  state: "on" | "off";
}

export function parseCircuitConfig(text: string): CircuitConfig {
  const s = text.toLowerCase();
  const component: CircuitConfig["component"] =
    /resistor|resistance|\bohm/.test(s) ? "resistor"
    : /\bmotor\b|\bfan\b/.test(s) ? "motor"
    : "bulb";
  // A circuit is ON unless the text says the switch is open / broken / off.
  const state: CircuitConfig["state"] =
    /\bopen\b|switch is open|\boff\b|broken|no current|disconnect|incomplete|gap/.test(s) ? "off" : "on";
  return { component, state };
}

/** Electrical-context signal — route these beats to the circuit, not a dot line. */
export const CIRCUIT_RE =
  /\bcircuit\b|\bbattery\b|\bvoltage\b|\bresistor\b|resistance|\bohm|\bLED\b|light bulb|\bbulb\b|electric current|current flows?|flow of current|electr(ons?|ical)|closed loop|short circuit|\bcapacitor\b|\banode\b|\bcathode\b/i;

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

export const CircuitActor: React.FC<VProps> = ({ items, box, theme, t }) => {
  const cfg = parseCircuitConfig(items.join(" "));
  const P = theme.palette;
  const on = cfg.state === "on";
  const wire = theme.dark ? "#9aa4b2" : "#5a6472";
  // width divisor is generous (/42) because the right-side leader labels extend
  // well past the loop — keeps "Switch"/"Battery" from clipping in a tall frame.
  const u = Math.min(box.h / 32, box.w / 42);

  // wire loop rectangle
  const halfW = 12 * u, halfH = 9 * u;
  const Lx = box.cx - halfW, Rx = box.cx + halfW, Ty = box.cy - halfH, By = box.cy + halfH;
  const W = Rx - Lx, H = By - Ty, perim = 2 * (W + H);

  // point on the loop perimeter, clockwise from top-left
  const ptOnRect = (f: number) => {
    let d = (((f % 1) + 1) % 1) * perim;
    if (d < W) return { x: Lx + d, y: Ty };
    d -= W;
    if (d < H) return { x: Rx, y: Ty + d };
    d -= H;
    if (d < W) return { x: Rx - d, y: By };
    d -= W;
    return { x: Lx, y: By - d };
  };

  // current dots — the verb. Move ~1.2 loops per beat; hidden when open.
  const N = 16;
  const dots = on
    ? Array.from({ length: N }, (_, i) => ptOnRect(t * 1.2 + i / N))
    : [];

  // glow of a lit bulb pulses gently
  const glow = on ? 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(t * TAU * 2)) : 0;

  const bulbC = { x: box.cx, y: Ty };            // load sits on the top edge
  const battC = { x: box.cx, y: By };            // battery on the bottom edge
  const swC = { x: Rx, y: box.cy };              // switch on the right leg

  return (
    <g>
      {/* wire loop (drawn first; components + dots sit on top) */}
      <rect x={Lx} y={Ty} width={W} height={H} rx={u * 0.6} fill="none" stroke={wire} strokeWidth={u * 0.45} />

      {/* current dots */}
      {dots.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={u * 0.5} fill={P.primary} opacity={0.9} />
      ))}

      {/* ── battery (bottom edge) ── */}
      <g>
        <rect x={battC.x - 6 * u} y={battC.y - u} width={12 * u} height={2 * u} fill={theme.dark ? "#0b0d10" : "#f4f6f8"} />
        {/* long plate (+) and short plate (−) */}
        <line x1={battC.x - 2 * u} y1={battC.y - 3 * u} x2={battC.x - 2 * u} y2={battC.y + 3 * u} stroke={wire} strokeWidth={u * 0.4} />
        <line x1={battC.x + 2 * u} y1={battC.y - 1.6 * u} x2={battC.x + 2 * u} y2={battC.y + 1.6 * u} stroke={wire} strokeWidth={u * 1.1} />
        <text x={battC.x - 4 * u} y={battC.y + 6.2 * u} fill={P.positive} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>+</text>
        <text x={battC.x + 4 * u} y={battC.y + 6.2 * u} fill={P.danger} fontSize={u * 2} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>−</text>
      </g>

      {/* ── load (top edge) ── */}
      <g>
        <rect x={bulbC.x - 5 * u} y={bulbC.y - u} width={10 * u} height={2 * u} fill={theme.dark ? "#0b0d10" : "#f4f6f8"} />
        {cfg.component === "resistor" ? (
          <path d={`M ${bulbC.x - 5 * u} ${bulbC.y} l ${1.2 * u} 0 l ${0.6 * u} ${-2 * u} l ${1.2 * u} ${4 * u} l ${1.2 * u} ${-4 * u} l ${1.2 * u} ${4 * u} l ${1.2 * u} ${-4 * u} l ${0.6 * u} ${2 * u} l ${1.2 * u} 0`}
            fill="none" stroke={wire} strokeWidth={u * 0.45} strokeLinejoin="round" />
        ) : cfg.component === "motor" ? (
          <>
            <circle cx={bulbC.x} cy={bulbC.y} r={4 * u} fill={theme.dark ? "#2e3640" : "#e6eaef"} stroke={wire} strokeWidth={u * 0.4} />
            <text x={bulbC.x} y={bulbC.y + 1.4 * u} fill={P.ink} fontSize={u * 3} fontWeight={800} textAnchor="middle" fontFamily={theme.body.family}>M</text>
          </>
        ) : (
          <>
            {/* glow halo when lit */}
            {glow > 0 && <circle cx={bulbC.x} cy={bulbC.y} r={7 * u} fill={P.highlight} opacity={0.25 * glow} />}
            <circle cx={bulbC.x} cy={bulbC.y} r={4 * u}
              fill={on ? P.highlight : (theme.dark ? "#2e3640" : "#e6eaef")}
              stroke={wire} strokeWidth={u * 0.4} opacity={on ? 0.55 + 0.45 * glow : 1} />
            {/* filament */}
            <path d={`M ${bulbC.x - 2 * u} ${bulbC.y + 1.5 * u} q ${1 * u} ${-4 * u} ${2 * u} 0 q ${1 * u} ${4 * u} ${2 * u} 0`}
              fill="none" stroke={on ? P.danger : wire} strokeWidth={u * 0.35} />
            {/* rays when lit */}
            {on && glow > 0.3 && Array.from({ length: 8 }, (_, i) => {
              const a = (i / 8) * TAU;
              return <line key={i} x1={bulbC.x + Math.cos(a) * 5.2 * u} y1={bulbC.y + Math.sin(a) * 5.2 * u}
                x2={bulbC.x + Math.cos(a) * 6.6 * u} y2={bulbC.y + Math.sin(a) * 6.6 * u}
                stroke={P.highlight} strokeWidth={u * 0.3} opacity={0.7 * glow} />;
            })}
          </>
        )}
      </g>

      {/* ── switch (right leg) ── */}
      <g>
        <rect x={swC.x - u} y={swC.y - 4 * u} width={2 * u} height={8 * u} fill={theme.dark ? "#0b0d10" : "#f4f6f8"} />
        <circle cx={swC.x} cy={swC.y + 3 * u} r={u * 0.5} fill={wire} />
        <circle cx={swC.x} cy={swC.y - 3 * u} r={u * 0.5} fill={wire} />
        {/* lever: down/closed touches both contacts; up/open leaves a gap */}
        <line x1={swC.x} y1={swC.y + 3 * u}
          x2={on ? swC.x : swC.x - 3 * u} y2={on ? swC.y - 3 * u : swC.y - 4.5 * u}
          stroke={wire} strokeWidth={u * 0.5} strokeLinecap="round" />
      </g>

      {/* labels (Rule 3) */}
      <Label tx={battC.x + 6 * u} ty={battC.y} lx={Rx + 3 * u} ly={By + 2 * u} text="Battery" theme={theme} u={u} />
      <Label tx={bulbC.x + (cfg.component === "bulb" ? 4 * u : 5 * u)} ty={bulbC.y} lx={Rx + 3 * u} ly={Ty - 2 * u}
        text={cfg.component === "resistor" ? "Resistor" : cfg.component === "motor" ? "Motor" : "Bulb"} theme={theme} u={u} />
      <Label tx={swC.x} ty={swC.y} lx={Rx + 4 * u} ly={box.cy} text={on ? "Switch" : "Switch (open)"} theme={theme} u={u} />

      {/* stage caption */}
      <text x={box.cx} y={By + 8.5 * u} fill={P.sub} fontSize={u * 1.8} fontWeight={700} textAnchor="middle" fontFamily={theme.body.family}>
        {on ? "Current flows — circuit closed" : "No current — circuit open"}
      </text>
    </g>
  );
};
